import { IValidation } from '@shared/common';

/**
 * Current version of the password policy defined by {@link passwordParameters}.
 *
 * @constant {number} PASSWORD_POLICY_VERSION
 * @default 2
 * @remarks
 * Increment when the password policy is tightened. Accounts with a lower stored
 * `passwordPolicyVersion` require a compliant password on their next login when the client
 * opts into password-change challenges via `supportsPasswordChallenge`.
 * Version 2 (2026-09) raises the minimum length from 8 to 12 and sets the maximum to 64 characters
 * (comfortably within the 72-byte bcrypt cap). Lowering the maximum does not itself require a version
 * bump — the max only gates NEW passwords; existing ones stay valid.
 * The character-class requirement is unchanged.
 * @see passwordParameters
 */
export const PASSWORD_POLICY_VERSION = 2;

/**
 * Maximum password length in BYTES enforced on top of the character `max`.
 *
 * @constant {number} BCRYPT_MAX_BYTES
 * @default 72
 * @remarks
 * bcrypt silently truncates its input at 72 bytes, so any password longer than that (e.g. a
 * 100-character password, or a shorter one with multi-byte UTF-8 characters) would have its tail
 * ignored at hash and compare time — advertising strength it does not carry. Server-side password
 * validation rejects inputs beyond this byte length rather than letting them be truncated.
 */
export const BCRYPT_MAX_BYTES = 72;

/**
 * Shared validation parameters for passwords under the current policy.
 *
 * @constant {Object.<string, IValidation>} passwordParameters
 * @property {IValidation} password - Password validation rules.
 * @property {number} password.min - Minimum password length: 12 characters.
 * @property {number} password.max - Maximum password length: 64 characters (within the 72-byte bcrypt cap).
 * @property {Object} password.match - Character-class validation rule and error message.
 * @property {RegExp} password.match.pattern - Anchored. Requires a digit, a lowercase and an
 * uppercase ASCII letter, AND restricts the whole password to the allowed set: ASCII letters,
 * digits, spaces and the symbols ! @ # $ % & * ? _ . - (so no other punctuation or non-ASCII).
 * @property {string} password.match.message - Validation message when the pattern does not match.
 * @see PASSWORD_POLICY_VERSION
 */
export const passwordParameters: { [key: string]: IValidation } = {
  password: {
    min: 12,
    max: 64,
    match: {
      pattern: /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])[A-Za-z0-9!@#$%&*?_. -]+$/,
      message:
        'Password must include a digit, a lowercase and an uppercase letter, and use only letters, digits, spaces or ! @ # $ % & * ? _ . -',
    },
  },
};
